import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Grid, Card, CardContent, Typography, TextField, Button,
  CircularProgress, Chip, Divider, IconButton, Tooltip,
} from "@mui/material";
import {
  Save, Refresh, Palette, Language, Email, CheckCircle,
  Error, HourglassEmpty, Preview,
} from "@mui/icons-material";
import { hubApi } from "../services/hubApi";

const DOMAIN_STATUS = {
  pending: { label: "Pendente", color: "#F59E0B", icon: HourglassEmpty },
  validating: { label: "Validando", color: "#6B48FF", icon: HourglassEmpty },
  active: { label: "Ativo", color: "#25D366", icon: CheckCircle },
  error: { label: "Erro", color: "#EF4444", icon: Error },
};

function ColorInput({ label, value, onChange }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
      <Box
        sx={{
          width: 36, height: 36, borderRadius: 2,
          bgcolor: value || "#6B48FF",
          border: "2px solid rgba(255,255,255,0.15)",
          cursor: "pointer", flexShrink: 0,
          position: "relative", overflow: "hidden",
        }}
      >
        <input
          type="color"
          value={value || "#6B48FF"}
          onChange={(e) => onChange(e.target.value)}
          style={{
            position: "absolute", inset: 0,
            width: "100%", height: "100%",
            opacity: 0, cursor: "pointer",
          }}
        />
      </Box>
      <TextField
        size="small"
        label={label}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder="#6B48FF"
        fullWidth
        InputProps={{ style: { color: "#fff", fontSize: 13 } }}
        InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
      />
    </Box>
  );
}

function BrandPreview({ config }) {
  const primary = config.primaryColor || "#6B48FF";
  const secondary = config.secondaryColor || "#FF6B00";
  const brandName = config.brandName || "OtimizAPP Connect";

  return (
    <Card sx={{ bgcolor: "#0D1117", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 3 }}>
      <CardContent sx={{ p: 0 }}>
        <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid rgba(255,255,255,0.06)", display: "flex", alignItems: "center", gap: 1 }}>
          <Preview sx={{ fontSize: 14, color: "#6B7280" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#9CA3AF" }}>Preview</Typography>
        </Box>

        {/* Mini preview do painel */}
        <Box sx={{ p: 2 }}>
          {/* Sidebar preview */}
          <Box sx={{ display: "flex", gap: 1.5, mb: 2 }}>
            <Box sx={{
              width: 60, borderRadius: 1.5, bgcolor: "#111827",
              border: "1px solid rgba(255,255,255,0.06)", p: 1,
            }}>
              <Box sx={{
                width: 20, height: 20, borderRadius: 1, mx: "auto", mb: 1,
                background: `linear-gradient(135deg, ${primary}, ${secondary})`,
              }} />
              {[1, 2, 3].map((i) => (
                <Box key={i} sx={{
                  height: 4, borderRadius: 1, mb: 0.5, mx: 0.5,
                  bgcolor: i === 1 ? primary + "30" : "rgba(255,255,255,0.06)",
                }} />
              ))}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box sx={{ height: 12, width: "50%", borderRadius: 1, bgcolor: primary + "20", mb: 1 }} />
              <Box sx={{ height: 8, width: "80%", borderRadius: 1, bgcolor: "rgba(255,255,255,0.04)", mb: 0.5 }} />
              <Box sx={{ height: 8, width: "60%", borderRadius: 1, bgcolor: "rgba(255,255,255,0.04)" }} />
            </Box>
          </Box>

          {/* Brand name */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
            {config.logoUrl ? (
              <img src={config.logoUrl} alt="" style={{ width: 24, height: 24, borderRadius: 4 }}
                onError={(e) => { e.target.style.display = "none"; }} />
            ) : (
              <Box sx={{
                width: 24, height: 24, borderRadius: 1,
                background: `linear-gradient(135deg, ${primary}, ${secondary})`,
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: 10, fontWeight: 800, color: "#fff",
              }}>
                {brandName.charAt(0)}
              </Box>
            )}
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>
              {brandName}
            </Typography>
          </Box>

          {/* Button preview */}
          <Box sx={{ display: "flex", gap: 1 }}>
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: 1.5, fontSize: 10,
              bgcolor: primary, color: "#fff", fontWeight: 600,
            }}>
              Primario
            </Box>
            <Box sx={{
              px: 1.5, py: 0.5, borderRadius: 1.5, fontSize: 10,
              bgcolor: secondary, color: "#fff", fontWeight: 600,
            }}>
              Secundario
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function WhiteLabelPage({ showSnackbar }) {
  const [config, setConfig] = useState({
    brandName: "", logoUrl: "", faviconUrl: "", primaryColor: "#6B48FF",
    secondaryColor: "#FF6B00", customDomain: "", domainStatus: "pending",
    supportEmail: "", supportPhone: "", loginText: "", loginBackground: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const loadConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data } = await hubApi.getWhiteLabel();
      setConfig({
        brandName: data.brandName || "",
        logoUrl: data.logoUrl || "",
        faviconUrl: data.faviconUrl || "",
        primaryColor: data.primaryColor || "#6B48FF",
        secondaryColor: data.secondaryColor || "#FF6B00",
        customDomain: data.customDomain || "",
        domainStatus: data.domainStatus || "pending",
        supportEmail: data.supportEmail || "",
        supportPhone: data.supportPhone || "",
        loginText: data.loginText || "",
        loginBackground: data.loginBackground || "",
      });
    } catch (err) {
      setError("Erro ao carregar configuracao");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadConfig(); }, [loadConfig]);

  const handleSave = async () => {
    try {
      setSaving(true);
      await hubApi.updateWhiteLabel(config);
      showSnackbar?.("Configuracao salva!");
    } catch {
      showSnackbar?.("Erro ao salvar", "error");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: "#6B48FF" }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ py: 4, textAlign: "center" }}>
        <Error sx={{ fontSize: 48, color: "#EF4444", mb: 1 }} />
        <Typography sx={{ color: "#EF4444" }}>{error}</Typography>
        <Button onClick={loadConfig} sx={{ mt: 2, color: "#6B48FF" }}>Tentar novamente</Button>
      </Box>
    );
  }

  const domainCfg = DOMAIN_STATUS[config.domainStatus] || DOMAIN_STATUS.pending;
  const DomainIcon = domainCfg.icon;

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: "#fff" }}>White Label</Typography>
          <Typography sx={{ fontSize: 13, color: "#6B7280" }}>
            Personalizar marca e identidade visual
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Tooltip title="Recarregar">
            <IconButton onClick={loadConfig} sx={{ color: "#6B7280" }}><Refresh /></IconButton>
          </Tooltip>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : <Save />}
            onClick={handleSave}
            disabled={saving}
            sx={{
              bgcolor: "#6B48FF", textTransform: "none", fontWeight: 600,
              borderRadius: 2, "&:hover": { bgcolor: "#5A3DE6" },
            }}
          >
            Salvar
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Coluna esquerda — Formulário */}
        <Grid item xs={12} md={7}>
          {/* Marca */}
          <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Palette sx={{ fontSize: 18, color: "#6B48FF" }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Marca</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField size="small" label="Nome da Marca" fullWidth
                    value={config.brandName} onChange={(e) => updateField("brandName", e.target.value)}
                    placeholder="Minha Empresa"
                    InputProps={{ style: { color: "#fff", fontSize: 13 } }}
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" label="URL do Logo" fullWidth
                    value={config.logoUrl} onChange={(e) => updateField("logoUrl", e.target.value)}
                    placeholder="https://meusite.com/logo.png"
                    InputProps={{ style: { color: "#fff", fontSize: 13 } }}
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <ColorInput label="Cor Primaria" value={config.primaryColor}
                    onChange={(v) => updateField("primaryColor", v)} />
                </Grid>
                <Grid item xs={6}>
                  <ColorInput label="Cor Secundaria" value={config.secondaryColor}
                    onChange={(v) => updateField("secondaryColor", v)} />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Domínio */}
          <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Language sx={{ fontSize: 18, color: "#6B48FF" }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Dominio</Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
                <TextField size="small" label="Dominio Customizado" fullWidth
                  value={config.customDomain} onChange={(e) => updateField("customDomain", e.target.value)}
                  placeholder="hub.minhaempresa.com.br"
                  InputProps={{ style: { color: "#fff", fontSize: 13 } }}
                  InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
                />
                <Chip
                  size="small"
                  icon={<DomainIcon sx={{ fontSize: 14, color: domainCfg.color + " !important" }} />}
                  label={domainCfg.label}
                  sx={{ bgcolor: domainCfg.color + "15", color: domainCfg.color, fontSize: 11, height: 28, flexShrink: 0 }}
                />
              </Box>
              <Typography sx={{ fontSize: 11, color: "#6B7280", mt: 1 }}>
                Configure um CNAME apontando para hub.otimizapp.com.br
              </Typography>
            </CardContent>
          </Card>

          {/* Suporte */}
          <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3, mb: 3 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                <Email sx={{ fontSize: 18, color: "#6B48FF" }} />
                <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff" }}>Suporte</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField size="small" label="E-mail de Suporte" fullWidth
                    value={config.supportEmail} onChange={(e) => updateField("supportEmail", e.target.value)}
                    placeholder="suporte@empresa.com"
                    InputProps={{ style: { color: "#fff", fontSize: 13 } }}
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField size="small" label="Telefone Suporte" fullWidth
                    value={config.supportPhone} onChange={(e) => updateField("supportPhone", e.target.value)}
                    placeholder="+55 11 99999-0000"
                    InputProps={{ style: { color: "#fff", fontSize: 13 } }}
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Login */}
          <Card sx={{ bgcolor: "#111827", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 3 }}>
            <CardContent>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff", mb: 2 }}>
                Tela de Login
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField size="small" label="Texto de Boas-vindas" fullWidth multiline rows={2}
                    value={config.loginText} onChange={(e) => updateField("loginText", e.target.value)}
                    placeholder="Bem-vindo ao nosso gateway de canais"
                    InputProps={{ style: { color: "#fff", fontSize: 13 } }}
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField size="small" label="URL Background Login" fullWidth
                    value={config.loginBackground} onChange={(e) => updateField("loginBackground", e.target.value)}
                    placeholder="https://meusite.com/bg.jpg"
                    InputProps={{ style: { color: "#fff", fontSize: 13 } }}
                    InputLabelProps={{ style: { color: "rgba(255,255,255,0.5)", fontSize: 12 } }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* Coluna direita — Preview */}
        <Grid item xs={12} md={5}>
          <Box sx={{ position: "sticky", top: 80 }}>
            <BrandPreview config={config} />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
